import { useState } from 'react';

interface Question {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

export default function SelfAssessment() {
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [showResults, setShowResults] = useState(false);

  const questions: Question[] = [
    {
      id: 1,
      question: '¿En qué plano anatómico ocurre principalmente una sentadilla tradicional?',
      options: [
        'Plano frontal (coronal)',
        'Plano sagital',
        'Plano transversal',
        'Todos los planos por igual'
      ],
      correctAnswer: 1,
      explanation: 'La sentadilla es principalmente un movimiento en plano sagital (flexión/extensión de cadera y rodilla). Aunque requiere estabilización en otros planos, el movimiento principal es sagital.'
    },
    {
      id: 2,
      question: 'El hombro es una articulación esférica. ¿Qué implica esto para el entrenamiento?',
      options: [
        'Puede soportar cargas muy altas sin riesgo',
        'Tiene gran movilidad pero requiere más trabajo de estabilización',
        'Solo puede moverse en un plano',
        'No necesita calentamiento específico'
      ],
      correctAnswer: 1,
      explanation: 'Las articulaciones esféricas como el hombro sacrifican estabilidad por movilidad. Por eso requieren trabajo específico de estabilización (manguito rotador, escapular) y progresión cuidadosa.'
    },
    {
      id: 3,
      question: 'El curl de bíceps es una palanca de tercer grado. ¿Qué significa esto?',
      options: [
        'El bíceps tiene ventaja mecánica y genera poca fuerza',
        'El bíceps debe generar fuerza mucho mayor que el peso de la mancuerna',
        'El ejercicio es más fácil al final del movimiento',
        'El fulcro está entre la fuerza y la resistencia'
      ],
      correctAnswer: 1,
      explanation: 'En palancas de tercer grado, la fuerza (músculo) está entre el fulcro y la resistencia. Esto crea desventaja mecánica: el músculo debe generar fuerza 7-8 veces mayor que la carga externa.'
    },
    {
      id: 4,
      question: '¿Cuándo es máximo el torque en la cadera durante una sentadilla?',
      options: [
        'En la posición más alta (de pie)',
        'A mitad del recorrido',
        'En el punto más bajo (máxima profundidad)',
        'El torque es constante durante todo el movimiento'
      ],
      correctAnswer: 2,
      explanation: 'El torque es máximo cuando el brazo de momento es mayor, lo cual ocurre en el punto más bajo de la sentadilla, donde el torso está más inclinado y la distancia horizontal entre la barra y la cadera es máxima.'
    },
    {
      id: 5,
      question: '¿Por qué es importante combinar ejercicios con diferentes curvas de fuerza?',
      options: [
        'Para hacer el entrenamiento más variado y entretenido',
        'Los ejercicios de curva ascendente dan fuerza y los de curva descendente dan tensión constante',
        'No es importante, solo importa el volumen total',
        'Para confundir al músculo y evitar adaptación'
      ],
      correctAnswer: 1,
      explanation: 'Los ejercicios compuestos (curva ascendente) permiten usar más carga y son mejores para fuerza. Los aislados (curva descendente) mantienen tensión constante en el músculo y son mejores para trabajo metabólico e hipertrofia.'
    }
  ];

  const handleAnswer = (questionId: number, answerIndex: number) => {
    setAnswers(prev => ({ ...prev, [questionId]: answerIndex }));
  };

  const calculateScore = () => {
    let correct = 0;
    questions.forEach(q => {
      if (answers[q.id] === q.correctAnswer) correct++;
    });
    return correct;
  };

  const score = calculateScore();
  const allAnswered = Object.keys(answers).length === questions.length;

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-2xl p-8 border border-gray-200">
        <div className="flex items-center gap-3 mb-6">
          <i className="ri-question-line text-3xl text-[#1A3A2E]"></i>
          <div>
            <h2 className="text-3xl font-bold text-black">Autoevaluación</h2>
            <p className="text-gray-600">Verifica tu comprensión de los conceptos clave</p>
          </div>
        </div>

        <div className="space-y-8">
          {questions.map((q, idx) => (
            <div key={q.id} className="bg-[#F5F2EB] rounded-xl p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-[#1A3A2E] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">{idx + 1}</span>
                </div>
                <h3 className="text-lg font-bold text-black leading-tight flex-1">
                  {q.question}
                </h3>
              </div>

              <div className="space-y-3 ml-14">
                {q.options.map((option, optionIdx) => {
                  const isSelected = answers[q.id] === optionIdx;
                  const isCorrect = optionIdx === q.correctAnswer;
                  const showFeedback = showResults && isSelected;

                  return (
                    <button
                      key={optionIdx}
                      onClick={() => !showResults && handleAnswer(q.id, optionIdx)}
                      disabled={showResults}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all cursor-pointer ${
                        showResults
                          ? isSelected && isCorrect
                            ? 'bg-green-50 border-green-500'
                            : isSelected && !isCorrect
                            ? 'bg-red-50 border-red-500'
                            : isCorrect
                            ? 'bg-green-50 border-green-500'
                            : 'bg-white border-gray-200'
                          : isSelected
                          ? 'bg-[#39FF14] border-[#39FF14]'
                          : 'bg-white border-gray-300 hover:border-[#39FF14]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            showResults
                              ? isCorrect
                                ? 'bg-green-500 border-green-500'
                                : isSelected
                                ? 'bg-red-500 border-red-500'
                                : 'border-gray-300'
                              : isSelected
                              ? 'bg-black border-black'
                              : 'border-gray-400'
                          }`}
                        >
                          {showResults && isCorrect && (
                            <i className="ri-check-line text-white text-sm"></i>
                          )}
                          {showResults && isSelected && !isCorrect && (
                            <i className="ri-close-line text-white text-sm"></i>
                          )}
                          {!showResults && isSelected && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                        <span className="text-base text-gray-700">{option}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {showResults && (
                <div className="ml-14 mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                  <p className="text-sm text-blue-900 leading-relaxed">
                    <strong>Explicación:</strong> {q.explanation}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-between">
          <div>
            {showResults && (
              <div className="flex items-center gap-3">
                <div className="text-4xl font-bold text-[#39FF14]">
                  {score}/{questions.length}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Respuestas correctas</p>
                  <p className="text-lg font-bold text-black">
                    {Math.round((score / questions.length) * 100)}% de acierto
                  </p>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => {
              if (showResults) {
                setAnswers({});
                setShowResults(false);
              } else {
                setShowResults(true);
              }
            }}
            disabled={!allAnswered && !showResults}
            className={`px-8 py-4 rounded-full font-medium transition-all whitespace-nowrap ${
              !allAnswered && !showResults
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : showResults
                ? 'bg-[#1A3A2E] text-white hover:bg-black cursor-pointer'
                : 'bg-[#39FF14] text-black hover:bg-[#2EE000] cursor-pointer'
            }`}
          >
            {showResults ? 'Reintentar' : 'Ver Resultados'}
          </button>
        </div>
      </div>

      {showResults && (
        <div
          className={`rounded-2xl p-8 ${
            score >= 4
              ? 'bg-gradient-to-r from-green-500 to-green-600'
              : score >= 3
              ? 'bg-gradient-to-r from-yellow-500 to-yellow-600'
              : 'bg-gradient-to-r from-red-500 to-red-600'
          }`}
        >
          <div className="text-center text-white">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <i
                className={`text-4xl ${
                  score >= 4
                    ? 'ri-trophy-line'
                    : score >= 3
                    ? 'ri-thumb-up-line'
                    : 'ri-book-line'
                }`}
              ></i>
            </div>
            <h3 className="text-2xl font-bold mb-2">
              {score >= 4
                ? '¡Excelente! Dominas los conceptos'
                : score >= 3
                ? 'Buen trabajo, repasa algunos conceptos'
                : 'Revisa el contenido del módulo'}
            </h3>
            <p className="text-white/90">
              {score >= 4
                ? 'Tienes una comprensión sólida de la biomecánica aplicada. Estás listo para el siguiente módulo.'
                : score >= 3
                ? 'Tienes una base buena, pero revisa las lecciones donde fallaste para reforzar conceptos.'
                : 'Te recomendamos revisar todo el contenido del módulo antes de continuar.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}